import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { ReactComponent as LogoWhite } from '@rocco/icons/svg/logo-new-white.svg';

import type { B2BNavLink } from '../types';
import { useLeadModal } from '../lead-form/useLeadModal';

export interface B2BTopNavProps {
    tagline: string;
    links: B2BNavLink[];
    /** Resolved path for the ARCHIPRO wordmark; falls back to the Rocco wordmark. */
    wordmarkSrc?: string;
    /** Hide the centred wordmark entirely (Figma `751:25159` index hero omits it). */
    hideWordmark?: boolean;
    /** Optional text-link CTA on the right side ("Book a Demo"). */
    secondaryCta?: B2BNavLink;
    /** Optional filled-pill CTA on the far right ("Apply to Join"). */
    primaryCta?: {
        label: string;
        href?: string;
        opensModal?: boolean;
    };
    className?: string;
}

/**
 * Transparent top nav overlaid on the hero (Figma node 461:24583 base, with
 * the `521:28034` variant adding the right-side CTAs). Positioned to the
 * 1920 frame: tagline far-left (~50px), nav link column at ~25%, ARCHIPRO
 * wordmark centred, and "Book a Demo" + "Apply to Join" cluster on the right
 * (right=60, top=24, gap=30). Text is 14 px Neue Haas Bold (0.73 vw).
 */
export const B2BTopNav = ({
    tagline,
    links,
    wordmarkSrc,
    hideWordmark = false,
    secondaryCta,
    primaryCta,
    className,
}: B2BTopNavProps) => {
    const { openModal } = useLeadModal();

    const handlePrimaryClick = primaryCta?.opensModal
        ? (e: React.MouseEvent) => {
              e.preventDefault();
              openModal({ ctaLabel: primaryCta.label });
          }
        : undefined;

    return (
        <nav
            className={cn(
                // Overlay the hero on both desktop and mobile so the photo
                // bleeds to the top edge (matches Figma mobile `521:26870`).
                'absolute inset-x-0 top-0 z-20 text-white max-md:h-[3.5rem] md:h-[12vw]',
                className,
            )}
        >
            {/* Tagline — far left.
             *  - Desktop (1920 frame): ~50 px left → 2.6vw.
             *  - Mobile (Figma `521:26870`, 768 frame): (27.46, 36) at 14 px
             *    Neue Haas Bold, width 274 px, leading 18 px.
             *    27.46 / 768 ≈ 3.575 %, 36 / 768 ≈ 4.69 vw, width 35.68 %, 14 / 768 ≈ 1.823 vw. */}
            <p
                className={cn(
                    'absolute font-bold',
                    'md:left-[2.6vw] md:top-[1.9vw] md:w-[14.3vw] md:text-[0.73vw] md:leading-[1.28] md:tracking-[-0.03em]',
                    'max-md:left-[3.575%] max-md:top-[4.69vw] max-md:w-[35.68%] max-md:text-[1.823vw] max-md:leading-[2.344vw] max-md:tracking-[-0.0547vw]',
                )}
            >
                {tagline}
            </p>

            {/* Nav links — column at ~25% from left. Desktop only. */}
            <ul className="absolute left-[25%] top-[1.82vw] flex flex-col text-[0.73vw] font-bold leading-[0.78vw] tracking-[-0.03em] max-md:hidden">
                {links.map((link) => (
                    <li key={`${link.label}-${link.href}`}>
                        <RoccoLink
                            to={link.href}
                            className="transition-opacity hover:opacity-70"
                        >
                            {link.label}
                        </RoccoLink>
                    </li>
                ))}
            </ul>

            {/* Wordmark — centred at top.
             *  - Desktop (1920 frame): top 2.55vw, height 1.24vw.
             *  - Mobile (Figma `521:26870`, 768 frame): (centre, 51) at
             *    183.75 × 23.84. 51/768 ≈ 6.64vw, 183.75/768 ≈ 23.93vw,
             *    23.84/768 ≈ 3.104vw. Scaling against the 768 frame keeps
             *    the wordmark from spilling into the tagline area at
             *    smaller phone viewports. */}
            {!hideWordmark && (
                <RoccoLink
                    to="/"
                    aria-label="ArchiPro home"
                    className="absolute left-1/2 -translate-x-1/2 max-md:top-[6.64vw] md:top-[2.55vw]"
                >
                    {wordmarkSrc ? (
                        <img
                            src={wordmarkSrc}
                            alt="ArchiPro"
                            className="max-md:h-[3.104vw] max-md:w-[23.93vw] md:h-[1.24vw] md:w-auto"
                        />
                    ) : (
                        <LogoWhite className="max-md:h-[3.104vw] max-md:w-[23.93vw] md:h-[1.24vw] md:w-[9.57vw]" />
                    )}
                </RoccoLink>
            )}

            {/* Right-side CTAs — Figma `521:28034`:
             *   - "Book a Demo" text link at right=60, top=24 (desktop only).
             *   - Primary pill (bg #181818, h=37, min-w=142, rounded).
             *   - Gap of 30 px between them, both vertically centred.
             *   Desktop sizing in vw against the 1920 frame; mobile uses the
             *   Phase-2 token (text-[3.31vw], h-[8vw]) so the pill is
             *   tap-friendly without crowding the centred wordmark. */}
            {(secondaryCta || primaryCta) && (
                <div className="absolute flex items-center max-md:right-[3.575%] max-md:top-[3.5vw] md:right-[3.125vw] md:top-[1.25vw] md:gap-[1.5625vw]">
                    {secondaryCta && (
                        <RoccoLink
                            to={secondaryCta.href}
                            className="whitespace-nowrap text-[0.73vw] font-bold leading-[0.78vw] tracking-[-0.03em] text-white transition-opacity hover:opacity-70 max-md:hidden"
                        >
                            {secondaryCta.label}
                        </RoccoLink>
                    )}
                    {primaryCta &&
                        (primaryCta.opensModal ? (
                            <button
                                type="button"
                                onClick={handlePrimaryClick}
                                className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#181818] font-bold tracking-[-0.03em] text-white shadow-[0_0.104vw_0.729vw_rgba(0,0,0,0.11)] transition-colors hover:bg-[#2a2a2a] max-md:h-[8vw] max-md:px-[3.5vw] max-md:py-[1vw] max-md:text-[3.31vw] max-md:leading-[1.07] md:h-[1.927vw] md:min-w-[7.396vw] md:px-[0.885vw] md:py-[0.365vw] md:text-[0.73vw] md:leading-[0.78vw]"
                            >
                                {primaryCta.label}
                            </button>
                        ) : (
                            (() => {
                                const href = primaryCta.href ?? '#';
                                const isExternal = /^https?:\/\//i.test(href);
                                return (
                                    <RoccoLink
                                        to={href}
                                        isNonRemixRoute={isExternal}
                                        newTab={isExternal}
                                        className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#181818] font-bold tracking-[-0.03em] text-white shadow-[0_0.104vw_0.729vw_rgba(0,0,0,0.11)] transition-colors hover:bg-[#2a2a2a] max-md:h-[8vw] max-md:px-[3.5vw] max-md:py-[1vw] max-md:text-[3.31vw] max-md:leading-[1.07] md:h-[1.927vw] md:min-w-[7.396vw] md:px-[0.885vw] md:py-[0.365vw] md:text-[0.73vw] md:leading-[0.78vw]"
                                    >
                                        {primaryCta.label}
                                    </RoccoLink>
                                );
                            })()
                        ))}
                </div>
            )}
        </nav>
    );
};
